import { z, type ZodType } from '@customerio/hydra-validation';
import { defineEntity } from '../shared/define-entity';

/**
 * The six SQL-import databases. These are the `?database=<type>` values the list
 * endpoint requires (it 400s without one), so the directory reads one query per
 * database and combines the results.
 */
export const SqlImportDatabase = {
  mysql: 'mysql',
  postgres: 'postgres',
  snowflake: 'snowflake',
  bigQuery: 'bigquery',
  redshift: 'redshift',
  sqlserver: 'sqlserver',
} as const;
export type SqlImportDatabase = (typeof SqlImportDatabase)[keyof typeof SqlImportDatabase];

/** All six database types, in declaration order. */
export const SQL_IMPORT_DATABASES: readonly SqlImportDatabase[] = Object.values(SqlImportDatabase);

/** The connected database an import belongs to (sideloaded via `sql_import_databases`). */
export interface SqlImportDatabaseRef {
  id: string;
  type: string;
  name: string;
  auth_type: string;
}

/**
 * A SQL import with its joined database attached. The list endpoint is an
 * untyped stub in the generated client; the envelope is
 * `{ sql_imports, sql_import_databases }` and each import references its database
 * by `database_id` (there is no scalar database_type on the row), so the join is
 * resolved here from the sideload. `database` is null when the sideload does not
 * carry the referenced database.
 */
export interface SqlImport {
  id: string;
  active: boolean;
  database_id: string;
  name: string;
  database: SqlImportDatabaseRef | null;
}

// Keys/pk only; the list response is parsed by `parseSqlImportsResponse` below
// (a per-row schema can't express the sideload join).
const SqlImportRead = z.looseObject({ id: z.string() }) as unknown as ZodType<SqlImport, unknown>;

/** @public — list params is the `?database=` discriminant, so each database has its own slot. */
export const sqlImport = defineEntity<SqlImport, SqlImport, SqlImportDatabase, undefined, 'workspace'>({
  name: 'sql_import',
  scope: 'workspace',
  pk: (s) => s.id,
  schema: { read: SqlImportRead },
});

export const sqlImportKeys = sqlImport.keys;

/**
 * Parse a `{ sql_imports, sql_import_databases }` list response, resolving each
 * import's `database_id` against the sideloaded databases.
 */
export function parseSqlImportsResponse(raw: unknown): SqlImport[] {
  const data = raw as { sql_imports?: unknown[]; sql_import_databases?: unknown[] };

  const databasesById = new Map<string, SqlImportDatabaseRef>();
  for (const entry of data.sql_import_databases ?? []) {
    const database = entry as Partial<Record<keyof SqlImportDatabaseRef, unknown>>;
    if (typeof database.id === 'string') {
      databasesById.set(database.id, {
        id: database.id,
        type: String(database.type ?? ''),
        name: String(database.name ?? ''),
        auth_type: String(database.auth_type ?? ''),
      });
    }
  }

  return (data.sql_imports ?? []).map((entry) => {
    const row = entry as { id?: unknown; active?: unknown; database_id?: unknown; name?: unknown };
    const databaseId = String(row.database_id ?? '');
    return {
      id: String(row.id ?? ''),
      active: row.active === true,
      database_id: databaseId,
      name: String(row.name ?? ''),
      database: databasesById.get(databaseId) ?? null,
    };
  });
}
